import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { GUITAR_RUNGS } from "@music-app/learning-system";
import { PageHeader } from "../components/PageHeader.js";

export function AboutPage(): ReactNode {
  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      <PageHeader
        eyebrow="about"
        title="Earwise"
        subhead="A music theory app that takes your ear seriously."
      />

      <div className="font-serif text-xl text-inkSoft space-y-5 leading-relaxed mb-12">
        <p>
          Most ear training apps drill you on intervals stripped of context. That&apos;s not how working
          musicians hear. You hear notes <em className="italic">in a key</em>, where each scale degree
          has a function and a pull. Earwise teaches that way.
        </p>
        <p>
          Nearly every exercise plays a short cadence first to establish tonality, then the target. You
          name what you heard by its scale function — not its absolute pitch. The key changes every
          question, so you can&apos;t memorize &quot;C is do.&quot; You learn to hear <em>do-ness</em> wherever it lives.
        </p>
        <p>
          Underneath, an algorithm called FSRS — the same spaced repetition math used by{" "}
          <a
            href="https://en.wikipedia.org/wiki/Anki"
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-ink/40 underline-offset-2 hover:text-accent transition-colors"
          >
            Anki
          </a>{" "}
          — tracks
          which scale degrees and intervals you struggle with, and brings them back at the moment
          just before you&apos;d forget.
        </p>
      </div>

      <div className="border-t border-ink/10 pt-12 space-y-14">
        <WhatMakesItDifferent />
        <TheMethod />
        <HowToUse />
        <TheCurriculum />
        <Credits />
      </div>
    </div>
  );
}

// ─── About sections ──────────────────────────────────────────────────────────

function WhatMakesItDifferent(): ReactNode {
  return (
    <section aria-label="What makes Earwise different">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-3">
        why earwise
      </p>
      <h2 className="font-serif text-3xl font-medium text-ink mb-4">
        Pitch <em className="italic text-accent">in context</em>, not in isolation.
      </h2>
      <p className="font-serif italic text-inkSoft leading-relaxed mb-3">
        Most ear-training apps drill intervals out of thin air — two beeps, name
        the gap. That teaches pattern-matching, not musical hearing. Real music
        lives inside a key, where every note carries a function: tonic, leading
        tone, raised fourth.
      </p>
      <p className="font-serif italic text-inkSoft leading-relaxed">
        Earwise establishes key context before every pitched exercise. A short
        cadence plays first so the tonic is felt, not assumed. Then the target
        sounds. You name what you hear relative to where you are — the way a
        musician actually thinks.
      </p>
    </section>
  );
}

const METHOD_PILLARS = [
  {
    title: "Cadence first.",
    body: "Nearly every exercise opens with a short cadence in the key. The key is established in your ear before anything else plays — so you're always hearing in context, never in a vacuum.",
  },
  {
    title: "Movable-do solfège.",
    body: "Scale degrees are labelled relative to the tonic: Do is always home, regardless of key. The same syllable carries the same feeling across every tonality — exactly how trained musicians think.",
  },
  {
    title: "Spaced repetition.",
    body: "The FSRS scheduler tracks every item you've heard and resurfaces it at the moment your memory is about to slip. Short daily sessions compound faster than long cramming sessions.",
  },
] as const;

function TheMethod(): ReactNode {
  return (
    <section aria-label="The Earwise method">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-3">
        the method
      </p>
      <h2 className="font-serif text-3xl font-medium text-ink mb-6">
        Three principles, one system.
      </h2>
      <div className="space-y-5">
        {METHOD_PILLARS.map((p) => (
          <div key={p.title} className="border-l-2 border-accent/30 pl-5">
            <p className="font-serif font-medium text-ink mb-1">{p.title}</p>
            <p className="font-serif italic text-inkSoft leading-relaxed text-sm">
              {p.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

const HOW_TO_STEPS = [
  {
    step: "01",
    title: "Take the placement test.",
    body: "A 2-minute adaptive test seeds your card deck from where you actually are — so the scheduler skips material you already own.",
    cta: { label: "Start placement →", to: "/placement" },
  },
  {
    step: "02",
    title: "Do your daily session.",
    body: "The coach builds a personalised plan each day — warm-up, due reviews, and a slice of new material — shaped by the focus plan you pick, from Standard to Harmony Focus or one you build yourself. Open it, work through it, done.",
    cta: { label: "Open today's plan →", to: "/review" },
  },
  {
    step: "03",
    title: "Fill gaps with free drills.",
    body: "Nine focused drills let you isolate any skill — scales, intervals, chord quality, inversions, cadences, modes, progressions, melodic and rhythmic dictation. No queue, no scheduling: configure and go, or switch to Challenge for a timed, scored run across any skill or mix. The guitar track adds a full fretboard hub — toggle it in Settings.",
    cta: { label: "Browse drills →", to: "/exercise" },
  },
] as const;

function HowToUse(): ReactNode {
  return (
    <section aria-label="How to use Earwise">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-3">
        how it works
      </p>
      <h2 className="font-serif text-3xl font-medium text-ink mb-6">
        Three steps to a sharper ear.
      </h2>
      <div className="space-y-6">
        {HOW_TO_STEPS.map((s) => (
          <div key={s.step} className="flex gap-5">
            <span className="font-mono text-2xl font-medium text-ink/20 tabular-nums shrink-0 w-8 pt-0.5">
              {s.step}
            </span>
            <div>
              <p className="font-serif font-medium text-ink mb-1">{s.title}</p>
              <p className="font-serif italic text-inkSoft leading-relaxed text-sm mb-3">
                {s.body}
              </p>
              <Link
                to={s.cta.to}
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:underline"
              >
                {s.cta.label}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const AUDIO_CREDITS = [
  {
    name: "Salamander Grand Piano",
    author: "Alexander Holm",
    license: "CC BY 3.0",
    url: "https://freepats.zenvoid.org/Piano/acoustic-grand-piano.html",
    note: "Piano samples used for all piano-instrument playback.",
  },
  {
    name: "University of Iowa MIS — Acoustic Guitar",
    author: "Lawrence Fritts, University of Iowa Electronic Music Studios",
    license: "No restrictions",
    url: "http://theremin.music.uiowa.edu/MIS.html",
    note: "Guitar samples used for all guitar-instrument playback.",
  },
] as const;

function Credits(): ReactNode {
  return (
    <section aria-label="Credits" className="pb-10">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-3">
        credits
      </p>
      <h2 className="font-serif text-3xl font-medium text-ink mb-6">
        What powers Earwise.
      </h2>

      <h3 className="font-serif font-medium text-ink mb-3">Scheduling</h3>
      <div className="border border-ink/20 p-4 bg-paperWarm mb-8">
        <a
          href="https://github.com/open-spaced-repetition"
          target="_blank"
          rel="noopener noreferrer"
          className="font-serif font-medium text-ink hover:text-accent transition-colors"
        >
          FSRS-4.5 — Free Spaced Repetition Scheduler
        </a>
        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-inkFaded mt-1 mb-2">
          Jarrett Ye &amp; the open-spaced-repetition community
        </p>
        <p className="font-serif italic text-inkSoft text-sm leading-relaxed">
          Earwise schedules reviews with the FSRS-4.5 algorithm — the same
          spaced-repetition scheduler adopted by Anki. Implemented independently
          for Earwise; not affiliated with or endorsed by either project.
        </p>
      </div>

      <h3 className="font-serif font-medium text-ink mb-3">Audio samples</h3>
      <div className="space-y-4">
        {AUDIO_CREDITS.map((c) => (
          <div key={c.name} className="border border-ink/20 p-4 bg-paperWarm">
            <a
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-serif font-medium text-ink hover:text-accent transition-colors"
            >
              {c.name}
            </a>
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-inkFaded mt-1 mb-2">
              {c.author} &middot; {c.license}
            </p>
            <p className="font-serif italic text-inkSoft text-sm leading-relaxed">
              {c.note}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

const EAR_TRAINING_TRACKS: Array<{ name: string; description: string }> = [
  {
    name: "Pitch & rhythm",
    description: "Hear a note in key and name where it sits in the scale — every degree in major and minor, plus the chromatic alterations. Rhythmic dictation lets you notate what you hear on a beat grid.",
  },
  {
    name: "Intervals",
    description: "Identify the distance between any two pitches: minor second to major seventh, ascending or descending, in and out of harmonic context.",
  },
  {
    name: "Harmony",
    description: "Chord quality, inversions, cadences, and progressions — from basic triads to sevenths, extended and altered dominant chords.",
  },
  {
    name: "Modes",
    description: "Recognise the six modes that colour away from major by their tonal character: Dorian, Phrygian, Lydian, Mixolydian, Aeolian, Locrian.",
  },
  {
    name: "Dictation",
    description: "Hear a short melody and reproduce it note by note. The ultimate integration test of pitch memory and key sense.",
  },
];

const GUITAR_TRACK = {
  name: "Guitar",
  description:
    "Project everything you hear onto the fretboard — hear a chord's function in key, then find it on the neck. The track climbs from single shapes to the whole CAGED system:",
} as const;

function TheCurriculum(): ReactNode {
  return (
    <section aria-label="The curriculum" className="pb-10">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-3">
        the curriculum
      </p>
      <h2 className="font-serif text-3xl font-medium text-ink mb-2">
        Five for the ear, one for the fretboard.
      </h2>
      <p className="font-serif italic text-inkSoft mb-8 leading-relaxed">
        Each track is an independent skill ladder. The scheduler works them in
        parallel — you&apos;ll always be moving forward on multiple fronts. Guitar
        is a separate track you can toggle anytime in Settings.
      </p>

      <h3 className="font-serif font-medium text-ink mb-3">Ear training</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {EAR_TRAINING_TRACKS.map((t) => (
          <div key={t.name} className="border p-4 border-ink/20 bg-paperWarm">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-serif font-medium text-ink">{t.name}</span>
            </div>
            <p className="font-serif italic text-inkSoft text-sm leading-relaxed">
              {t.description}
            </p>
          </div>
        ))}
      </div>

      <h3 className="font-serif font-medium text-ink mt-8 mb-3">Guitar</h3>
      <div className="border p-4 border-ink/20 bg-paperWarm">
        <span className="font-serif font-medium text-ink">{GUITAR_TRACK.name}</span>
        <p className="font-serif italic text-inkSoft text-sm leading-relaxed mt-1 mb-3">
          {GUITAR_TRACK.description}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {GUITAR_RUNGS.map((r) => (
            <span
              key={r.id}
              className="inline-block font-mono text-[10px] uppercase tracking-[0.12em] text-inkFaded border border-ink/15 px-2 py-0.5"
            >
              {r.label}
            </span>
          ))}
        </div>
        <p className="font-serif italic text-inkSoft text-sm leading-relaxed mb-3">
          Toggle the guitar track anytime in Settings.
        </p>
        <Link
          to="/settings"
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:underline"
        >
          Manage in Settings →
        </Link>
      </div>
    </section>
  );
}
